/**
 * Trunnion AI - Onboarding Tour
 * Main tour orchestrator that consumes TourProvider context and renders the
 * current TourStep. When the tour completes, a brief "All set!" celebration
 * message is shown before fading out.
 */

import { useContext, useEffect, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { TourContext } from '@/components/onboarding/TourProvider';
import { TourStep } from '@/components/onboarding/TourStep';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OnboardingTourProps {
  /** Whether the celebration overlay should be visible */
  showCelebration: boolean;
  /** Called when the celebration overlay should be dismissed */
  onDismissCelebration: () => void;
}

// ---------------------------------------------------------------------------
// Celebration Component
// ---------------------------------------------------------------------------

function CelebrationOverlay({ onDismiss }: { onDismiss: () => void }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      // Allow the fade-out animation to complete before removing from the DOM
      const cleanup = setTimeout(onDismiss, 300);
      return () => clearTimeout(cleanup);
    }, 2500);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      className={`fixed inset-0 z-10002 flex items-center justify-center transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ backgroundColor: 'rgba(10, 14, 23, 0.8)' }}
      role="status"
      aria-live="polite"
    >
      <div className="animate-fade-in flex flex-col items-center gap-3 rounded-xl border border-trunnion-gold-500/30 bg-trunnion-slate-800 px-10 py-8 shadow-glow-gold">
        <CheckCircle className="h-12 w-12 text-trunnion-gold-500" />
        <h2 className="text-xl font-bold text-white">All set!</h2>
        <p className="text-sm text-trunnion-slate-300">
          You&apos;re ready to start using Trunnion AI.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function OnboardingTour({ showCelebration, onDismissCelebration }: OnboardingTourProps) {
  const ctx = useContext(TourContext);

  // If the context is not available (i.e. rendered outside TourProvider during
  // the initial mount), render nothing.  The TourProvider itself renders this
  // component, so context will be populated on subsequent renders.
  if (!ctx) {
    // Still render celebration if requested (it's context-independent)
    return showCelebration ? <CelebrationOverlay onDismiss={onDismissCelebration} /> : null;
  }

  const { isActive, currentStep, currentStepIndex, totalSteps, next, prev, skip } = ctx;

  return (
    <>
      {/* Active tour step */}
      {isActive && currentStep && (
        <TourStep
          key={currentStep.id}
          target={currentStep.target}
          title={currentStep.title}
          description={currentStep.description}
          placement={currentStep.placement}
          stepIndex={currentStepIndex}
          totalSteps={totalSteps}
          showBack={currentStepIndex > 0}
          onNext={next}
          onBack={prev}
          onSkip={skip}
        />
      )}

      {/* Celebration overlay */}
      {showCelebration && <CelebrationOverlay onDismiss={onDismissCelebration} />}
    </>
  );
}
